import { cdkLottery } from '@tencent/wphk-act-base-cgi';
import { isLimitErrCode } from '@tencent/wphk-act-base-framework';
import { to, url } from '@tencent/wphk-base-utils';
import { getServerTime } from '@tencent/wphk-components-common/utils/server-time';
import dayjs from 'dayjs';
import { computed, ref } from 'vue';
import { LOTTERY_ADTAG_WHATSAPP, LOTTERY_END_TIME, LOTTERY_START_TIME } from '../config';
import { LotteryExecErrCode, LotteryExecStatus } from '../types';
import { hasJoinLottery, setLotteryStatus } from '../utils/storage';
import { hideToast, showToast, ToastType } from '@tencent/wphk-components-toast';
import { CustomException } from '@tencent/ppd-base-libs';
/**
 * Banner 状态枚举
 */
export enum BannerStatus {
  /** 进行中 (未中奖/未开始) */
  Ongoing = 'ongoing',
  /** 已结束 */
  Ended = 'ended',
  /** 已中奖 */
  Won = 'won',
}


export const useVitasoyLottery = ({ cdkey }: { cdkey: string }) => {
  const isActStart = computed(() => dayjs(getServerTime()).isAfter(LOTTERY_START_TIME));
  const isActEnd = computed(() => dayjs(getServerTime()).isAfter(LOTTERY_END_TIME));
  const getBannerStatus = () => {
    // 活动已结束
    if (isActEnd.value) return BannerStatus.Ended;

    // 检查是否已中奖
    const hasWon = hasJoinLottery();
    if (hasWon) return BannerStatus.Won;

    // 活动进行中（未中奖）
    return BannerStatus.Ongoing;
  };
  /**
   * Banner 状态计算
   * - ONGOING: 活动进行中（未中奖）
   * - WON: 已中奖
   * - ENDED: 活动已结束
   */
  const bannerStatus = ref<BannerStatus>(getBannerStatus());

  const isWhatsappAdtag = computed(() => url.getUrlAdtag() === LOTTERY_ADTAG_WHATSAPP);
  /**
   * 自动执行抽奖
   * 在 onMounted 时如果 cdkey 存在且未抽奖，自动执行抽奖
   */
  const executeAutoLottery = async () => {
    // 如果不是whatsapp adtag，不执行自动抽奖
    if (!isWhatsappAdtag.value) {
      return;
    }
    // 如果是whatsapp adtag，但是cdkey为空，抛出异常
    if (isWhatsappAdtag.value && !cdkey) {
      new CustomException(null, 'useVitasoyLottery', 'cdkey is empty');
      return;
    }
    const [err, res] = await to(execLottery());
    if (err || !res) {
      throw err;
    }
    // 设置中奖状态
    setLotteryStatus(cdkey);
    bannerStatus.value = getBannerStatus();
    return res;
  };

  const execLottery = async () => {
    if (isActEnd.value) {
      throw { retcode: LotteryExecStatus.end };
    }
    showToast({ title: '抽獎中', type: ToastType.Empty, className: 'lottery-toast', isAutoHide: false });
    const [err, res] = await to(cdkLottery({ cdk: cdkey }));
    hideToast();
    if (err || !res) {
      if (!err?.retcode) {
        throw err;
      }
      /** 命中限额 */
      if (isLimitErrCode(err.retcode)) {
        throw { retcode: LotteryExecStatus.limit };
      }
      /** 活动已结束 */
      if (err.retcode === LotteryExecErrCode.end) {
        throw { retcode: LotteryExecStatus.end };
      }
      /** cdkey已被使用 */
      if ([LotteryExecErrCode.usedMyself, LotteryExecErrCode.usedOthers].includes(err.retcode as LotteryExecErrCode)) {
        throw { retcode: LotteryExecStatus.used };
      }
      if (err.retcode === LotteryExecErrCode.wrongCdkey) {
        throw { retcode: LotteryExecStatus.wrongCdkey };
      }
      // 其他错误码
      throw err;
    }
    if (res.prizeId === 'null') {
      /** 抽到空奖，代表已派完 */
      throw { retcode: LotteryExecStatus.soldOut };
    }
    return res;
  };
  return {
    isActStart,
    isActEnd,
    bannerStatus,
    executeAutoLottery,
    execLottery,
  };
};
